/**
 * The GLOBAL header readout for the A/D hotkey target.
 *
 * One compact line beside the one-click arm control that answers, at a
 * glance, "where would A/D land right now?" — the last EXPLICITLY AIMED card
 * in human terms, a truthful state, and the disengage control:
 *
 *   armed, no target   →  "A/D — aim a game"           (teaches the chip)
 *   …opted into Pull All →  "A/D · Spacebar — aim a game"  (#802: the pull
 *                            key needs the SAME gesture, and nothing said so)
 *   both actionable    →  "⌨ NYJ ML · Ready A/D  ×"
 *   one actionable     →  "⌨ NYJ ML · Ready A  ×"  (or Ready D)
 *   neither            →  "⌨ NYJ ML · Unavailable  ×"
 *   Paused (expanded)  →  "⏸ NYJ ML · Paused: expanded  ×"  (takes precedence)
 *   resolver gone      →  "A/D target unavailable  ×"
 *
 * "Actionable" is derived from the SAME resolved targets a keypress would
 * fire through: the side exists, displays a price, and its line is active.
 * A readout that said "Ready" over two null prices would be teaching the
 * trader to trust a dead target — presentation-only, but never untruthful.
 *
 * SINGLE SOURCE OF TRUTH: everything renders from the existing
 * `lt2FocusStore` — `focusedCardId` + `resolveFocused()` (the same resolver
 * the keypress fires through). There is no second target state anywhere:
 * `presentationSeq` is only an invalidation signal the FOCUSED card bumps on
 * its own re-renders, so label/paused changes appear without polling. The
 * readout renders nothing while disarmed or without the hotkey entitlement —
 * disarming already clears the focus (the listener's subscription), so an
 * idle page shows an idle header.
 *
 * The ✕ reuses `clearFocus` (the badge's own action) and carries
 * `data-lt2-hotkey-control` + a self-blur, so pressing A right after
 * disengaging is a silent no-op rather than an "owned control" rejection.
 */
import { useDisplaySettings } from '@/stores/displaySettings';
import {
  hotkeyLabelFor,
  hotkeyPairLabel,
  isActionUnbound,
} from '@/lib/trading/liveHotkeyRegistry';
import { useLiveHotkeyGrants, useLiveHotkeyMap } from '@/hooks/useLiveHotkeys';
import { useLt2FocusStore, type Lt2HotkeyTarget } from '@/stores/lt2FocusStore';
import { useOneClickStore } from '@/stores/oneClickStore';
import { useLt2Access } from '@/hooks/useLt2Access';
import { cn } from '@/lib/infra/utils';

export function Lt2TargetReadout() {
  const lt2Access = useLt2Access();
  const hotkeysEnabled = useDisplaySettings((s) => s.lt2HotkeysEnabled);
  const armed = useOneClickStore((s) => s.armed);
  // #802 — the pull key is named here only while the trader has opted in.
  // Off, Space is an ordinary browser key and naming it would advertise a
  // capability this trader does not have.
  const pullAllEnabled = useDisplaySettings((s) => s.lt2PullAllEnabled);
  // #725 — every key this readout names is the signed-in trader's own.
  const hotkeys = useLiveHotkeyMap();
  const grants = useLiveHotkeyGrants();
  const focusedCardId = useLt2FocusStore((s) => s.focusedCardId);
  // Subscribe to the focused card's render announcements so Ready/Paused and
  // the label re-derive exactly when they may have changed.
  useLt2FocusStore((s) => s.presentationSeq);

  if (!lt2Access || !hotkeysEnabled || !armed) return null;

  const pair = hotkeyPairLabel(hotkeys, 'passive-top', 'passive-bottom', grants);
  const topKey = hotkeyLabelFor(hotkeys, 'passive-top', grants);
  const bottomKey = hotkeyLabelFor(hotkeys, 'passive-bottom', grants);

  const targets =
    focusedCardId != null ? useLt2FocusStore.getState().resolveFocused() : null;

  if (focusedCardId == null) {
    // #802 — the unaimed line named A/D only, so an opted-in trader had
    // nothing on screen telling them Pull All needs the same gesture. Asked
    // through `isActionUnbound` rather than by comparing the label to an em
    // dash: a trader who remapped the pull away from Space holds no pull key,
    // and a readout must never name one that fires nothing.
    const namesPull = pullAllEnabled && !isActionUnbound(hotkeys, 'pull-all', grants);
    const keys = namesPull
      ? `${pair} · ${hotkeyLabelFor(hotkeys, 'pull-all', grants)}`
      : pair;
    return (
      <span
        data-testid="lt2-target-readout"
        // min-w-0 + wrapping, not shrink-0: this line grew a second key label
        // and the widest legal one is "Spacebar". A shrink-0 span would push
        // the arm control and the size input out of the cluster at 1470
        // rather than take a second line (cf. OneClickArmControl's bounded
        // ack region, which learned the same lesson the hard way).
        className="text-muted-foreground min-w-0 max-w-[240px] whitespace-normal break-words font-mono text-[11px] leading-tight"
        title={
          namesPull
            ? `${pair} and ${hotkeyLabelFor(hotkeys, 'pull-all', grants)} are armed but not aimed. Click a moneyline game's ⌨ chip, or a spread/total row, to aim them. Pull All cancels on the aimed game only.`
            : `${pair} hotkeys are armed but not aimed. Click a moneyline game's ⌨ chip, or a spread/total row, to aim them.`
        }
      >
        {keys} — aim a game
      </span>
    );
  }

  const clear = (
    <button
      type="button"
      data-lt2-hotkey-control=""
      onClick={(e) => {
        e.currentTarget.blur();
        useLt2FocusStore.getState().clearFocus();
      }}
      className="text-muted-foreground hover:text-foreground shrink-0 rounded px-0.5"
      aria-label="Disengage hotkey target"
      title={`Disengage — ${pair} stop targeting this card. Esc does the same.`}
    >
      ×
    </button>
  );

  if (!targets) {
    // Focused id with no live resolver: reachable only inside a teardown
    // commit (register()'s cleanup clears synchronously) or when a resolver
    // reports its market gone. Truthful, never pretend-Ready.
    return (
      <span
        data-testid="lt2-target-readout"
        className="flex min-w-0 shrink-0 items-center gap-1 font-mono text-[11px] text-amber-400"
      >
        <span title={`The aimed card is no longer resolvable — the next ${pair} press clears it.`}>
          {pair} target unavailable
        </span>
        {clear}
      </span>
    );
  }

  // A side is actionable exactly when the keypress guard would let it fire:
  // it exists, shows a price, and its line is active.
  const actionable = (t: Lt2HotkeyTarget | null): boolean =>
    t != null && t.priceCents != null && t.availability === 'active';
  const aReady = actionable(targets.a);
  const dReady = actionable(targets.d);
  const paused = targets.pausedReason === 'expanded';
  // #725 — the readiness DISCRIMINANT is kept separate from the rendered
  // string. The old code recovered the key with `state.slice(-1)`, which is
  // only correct while the keys are single letters called A and D; a remap to
  // "Space" would have made the tooltip name the letter "e".
  const readiness: 'paused' | 'both' | 'top' | 'bottom' | 'none' = paused
    ? 'paused'
    : aReady && dReady
      ? 'both'
      : aReady
        ? 'top'
        : dReady
          ? 'bottom'
          : 'none';
  const state =
    readiness === 'paused'
      ? 'Paused: expanded'
      : readiness === 'both'
        ? `Ready ${pair}`
        : readiness === 'top'
          ? `Ready ${topKey}`
          : readiness === 'bottom'
            ? `Ready ${bottomKey}`
            : 'Unavailable';
  const degraded = readiness === 'paused' || readiness === 'none';
  const title =
    readiness === 'paused'
      ? `${pair} are aimed at ${targets.cardLabel} but paused while its card is expanded — collapse the row to resume.`
      : readiness === 'none'
        ? `${pair} are aimed at ${targets.cardLabel}, but neither side shows an active priced line right now — a press would be refused truthfully.`
        : readiness === 'both'
          ? `${pair} are aimed at ${targets.cardLabel} at the visible one-click size.`
          : `Only ${readiness === 'top' ? topKey : bottomKey} has an active priced line on ${targets.cardLabel}; the other side would be refused truthfully.`;
  return (
    <span
      data-testid="lt2-target-readout"
      className={cn(
        'flex min-w-0 max-w-[240px] items-center gap-1 break-words font-mono text-[11px]',
        degraded ? 'text-amber-400' : 'text-primary',
      )}
    >
      <span title={title}>
        {paused ? '⏸' : '⌨'} {targets.cardLabel} · {state}
      </span>
      {clear}
    </span>
  );
}
